import React, { useContext, useEffect, useMemo, useState } from "react";

import { Badge, Button, Money, StatePanel, Toggle } from "../../components/ui";
import Icon from "../../components/ui/Icon";
import { getFileUrl } from "../../core/config/api.js";
import { useDebouncedValue } from "../../core/hooks/useDebouncedValue";
import { boxLabels, conditionLabels } from "../../product/constants";
import TradeFeedCard from "../../tradeFeed/TradeFeedCard.jsx";
import TradeFeedModal from "../../tradeFeed/TradeFeedModal.jsx";
import useCoreListMatches from "../hooks/useCoreListMatches.js";
import { UserContext } from "../../contexts/User.jsx";
import CoreListTradeFeed from "./CoreListTradeFeed.jsx";
import "../../styles/trade-feed.scss";
import "./core-list-match-view.scss";

/**
 * Every trade quote for ONE Core List reference, as the Trade Feed cards they
 * came from (Alex, 2026-10-12).
 *
 * The matches used to be a price table inside the target panel, which is the
 * wrong shape twice over. A row of numbers says a dealer quoted £47,610 and
 * nothing about WHO, in which group, with what photo, in what words — so a
 * match could be read but not acted on. And the panel is a column inside a
 * card grid, which is no place for a list that ran to 945 entries.
 *
 * So the matches moved out into their own full-width view, and the cards are
 * the SAME component the feed renders — imported, not reimplemented, so the
 * two can never drift. The only addition is the price strip: what our pipeline
 * read out of this message, which is the one thing the feed has no reason to
 * show.
 *
 * `leads-dark` on the root is load-bearing, not decoration: the `--rc-*` token
 * block in _leads-dark-theme.scss is scoped to it, and that is where the card's
 * surfaces, hairlines and type come from in both themes.
 */

/** Bucket ids are the API's own, so the segmented control IS the query. */
const BUCKETS = [
  { value: "at_target", label: "At or under target" },
  { value: "unknown_year", label: "Year unknown" },
  { value: "above", label: "Not in target" },
  { value: "all", label: "All Approved" },
];

/**
 * product_prices.condition and .box are JSON ARRAYS stored as strings, so a
 * row rendered them raw as ["good"] (Alex, 2026-10-09). The product page has
 * read them this way since long before Core List existed; same treatment here
 * so one quote reads identically in both places.
 *
 * Anything unparseable renders nothing rather than the raw string: a blank is
 * honest, a bracketed fragment is noise that looks like a bug.
 */
const labelList = (value, labels) => {
  if (!value) return null;

  let items = value;

  if (typeof value === "string") {
    try {
      items = JSON.parse(value);
    } catch (_) {
      return null;
    }
  }

  if (!Array.isArray(items) || items.length === 0) return null;

  return items.map((item) => labels[item] || item).join(", ");
};

/**
 * What our pipeline extracted from this message — the one thing the feed card
 * does not carry and the only reason this view is not just Trade Feed.
 *
 * `price` is MINOR units, like every product_prices figure, so no `major`
 * flag. Flagging it major is what printed a £47,610 quote as £4,761,000 on
 * 2026-10-09.
 */
const PriceStrip = ({ row }) => {
  const spec = [
    labelList(row.condition, conditionLabels),
    labelList(row.box, boxLabels),
    row.location,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
  <div className="cl-match__price">
    <span className="cl-match__price-main">
      <Money value={row.price} currency={row.currency} decimals={0} />
      {row.currency !== row.base_currency && (
        <span className="cl-match__price-conv">
          ≈ <Money value={row.price_base} currency={row.base_currency} decimals={0} tone="muted" />
        </span>
      )}
    </span>

    <span className="cl-match__price-meta">
      {row.year ? <span className="cl-match__year">{row.year}</span> : <span className="cl-match__year is-unknown">Year unknown</span>}

      {row.target_max_price != null && (
        <span className="cl-match__target">
          {/* `major` HERE and nowhere else on this card:
              core_list_price_targets.max_price really is whole units. */}
          target <Money value={row.target_max_price} major currency={row.target_currency} decimals={0} />
        </span>
      )}

      {row.under_median_pct != null && row.under_median_pct > 0 && (
        <Badge tone="success" size="sm">
          {row.under_median_pct}% under median
        </Badge>
      )}
    </span>

    {/* Condition and box decide whether a price under target is actually a
        good price, so they are READ here beside the figure — which is where
        they were always more useful than as a filter on the watch (the
        Condition filter was removed on 2026-10-12 for exactly that reason). */}
    {spec && <span className="cl-match__spec">{spec}</span>}
  </div>
  );
};

/** The feed's own period vocabulary, so "Any time" means the same on both. */
const PERIODS = [
  { value: "", label: "Any time" },
  { value: "1d", label: "Last 24 hours" },
  { value: "3d", label: "Last 3 days" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

const CoreListMatchView = ({ itemId, reference = "", hasTargets = true }) => {
  // No target → no buckets: the API files every quote as unmeasured, so the
  // view shows them all (Guillaume, 2026-10-12: see the prices before setting
  // the target). Saving a target flips it to the at-target bucket.
  const [bucket, setBucket] = useState(hasTargets ? "at_target" : "all");
  // The Trade Feed tab: every feed message mentioning the reference, not
  // only the approved prices (Alex, 2026-10-20). Master-admin only because
  // /api/trade-feed is; a store user would get a 403 in a tab.
  const canSeeFeed = !!useContext(UserContext)?.user?.is_master_admin;
  const [feedTab, setFeedTab] = useState(false);
  const [underpriced, setUnderpriced] = useState(false);
  const [band, setBand] = useState("");
  // WHAT IS IN THE BOX, which is not what is queried. Every keystroke used to
  // change the query key, so the grid refetched and dimmed on each letter —
  // which is what "the search bar keeps reloading" was (Alex, 2026-10-13).
  // The same split CoreListTable makes between `searchText` and `query`.
  const [q, setQ] = useState("");

  // WHAT IS ACTUALLY QUERIED. 350ms of stability, so a Mongo lookup runs once
  // per pause rather than once per letter.
  const debouncedQ = useDebouncedValue(q.trim(), 350);
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState(null);

  // The feed's filters, applied to the same messages (Alex, 2026-10-12).
  // Country and region are ONE piece of state: a region is a set of countries,
  // and the backend only speaks `countries[]`, so picking either resolves to
  // the same list and the two choosers can never contradict each other.
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [period, setPeriod] = useState("");
  const [hiddenGroups, setHiddenGroups] = useState([]);
  const [mediaAndText, setMediaAndText] = useState(false);
  const [hideBulk, setHideBulk] = useState(false);

  const filters = useMemo(
    () => ({
      bucket,
      underpriced,
      band,
      q: debouncedQ,
      country,
      region,
      period,
      hiddenGroups,
      mediaAndText,
      hideBulk,
      page,
      perPage: 24,
    }),
    [
      bucket,
      underpriced,
      band,
      debouncedQ,
      country,
      region,
      period,
      hiddenGroups,
      mediaAndText,
      hideBulk,
      page,
    ]
  );

  // A new search starts at page 1, keyed off the DEBOUNCED value — doing it
  // per keystroke would reset the page while the old results were still the
  // ones on screen.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ]);

  const {
    items,
    total,
    counts,
    bands,
    medianBase,
    baseCurrency,
    facetCountries,
    facetGroups,
    productImageFileId,
    isFetching,
    isError,
  } = useCoreListMatches(itemId, filters);

  // Every filter change restarts at page 1. Without this, narrowing a 40-page
  // result while on page 12 lands on an empty page that reads as "no matches".
  const change = (fn) => (value) => {
    setPage(1);
    fn(value);
  };

  useEffect(() => {
    setBucket(hasTargets ? "at_target" : "all");
    setPage(1);
  }, [hasTargets]);

  // Regions are DERIVED from the countries the facets reported, not fetched:
  // /api/trade-feed/regions is master-admin only and Core List is store-scoped.
  // Each country carries the region it belongs to, so the chooser is built from
  // the same list the Country chooser uses and the two cannot disagree.
  const regions = useMemo(() => {
    const byName = new Map();

    facetCountries.forEach((c) => {
      if (!c.region) return;
      const entry = byName.get(c.region) || {
        key: c.region,
        label: c.region_label || c.region,
        count: 0,
      };
      entry.count += c.count;
      byName.set(c.region, entry);
    });

    return [...byName.values()].sort((a, b) => b.count - a.count);
  }, [facetCountries]);

  const pages = Math.max(1, Math.ceil(total / 24));

  // Resolved once per render, not per card: 24 identical calls to build the
  // same URL is 24 more than this needs.
  const productImageUrl = productImageFileId ? getFileUrl(productImageFileId) : null;

  return (
    <div className="leads-dark cl-match">
      {/* No title and no Close of its own: this is a TAB inside the reference
          panel now (Alex, 2026-10-12), and the panel header already carries
          both. A second heading and a second close button would read as a
          nested dialog that is not one. */}
      {!feedTab && (
      <p className="cl-match__sub">
        {hasTargets
          ? "The WhatsApp messages these prices were read from, newest first."
          : "Every trade-group quote for this reference, newest first. Set a target to sort them into what is at your price."}
        {medianBase != null && (
          <>
            {" "}
            Trades at <Money value={medianBase} currency={baseCurrency} decimals={0} /> across the
            matched quotes.
          </>
        )}
      </p>
      )}

      <div className="cl-match__filters">
        {/* The page's OWN pill row — Button variant="tab" inside
            .core-list-pills — and not the Segmented control (Alex,
            2026-10-12: "I don't like the tab styling"). Segmented is a
            settings control and looked like one. These are the same pills the
            Core List filter row already uses, so one page has one way of
            showing a choice.

            "All Approved" carries no count: it is the sum of the other three,
            and repeating the total adds nothing. */}
        {(hasTargets || canSeeFeed) && (
        <div
          className="core-list-pills core-list-pills--filters cl-match__buckets"
          role="group"
          aria-label="Which quotes to show"
        >
          {/* No target: "All Approved" is the only bucket there is, offered
              beside Trade Feed so the reader can get back to it. */}
          {(hasTargets ? BUCKETS : BUCKETS.filter((b) => b.value === "all")).map((b) => (
            <Button
              key={b.value}
              type="button"
              variant="tab"
              size="md"
              active={!feedTab && bucket === b.value}
              aria-pressed={!feedTab && bucket === b.value}
              onClick={() => {
                setFeedTab(false);
                change(setBucket)(b.value);
              }}
            >
              {b.label}
              {b.value === "all" ? "" : " " + (counts[b.value] ?? 0).toLocaleString()}
            </Button>
          ))}
          {canSeeFeed && (
            <Button
              type="button"
              variant="tab"
              size="md"
              active={feedTab}
              aria-pressed={feedTab}
              onClick={() => setFeedTab(true)}
            >
              Trade Feed
            </Button>
          )}
        </div>
        )}

        {!feedTab && (
        <>
        <div className="cl-match__filter-row">
          <label className="cl-match__search">
            <Icon name="Search" size="xs" />
            <input
              type="search"
              value={q}
              placeholder="Keyword — e.g. full set, naked, HKD"
              onChange={(e) => setQ(e.target.value)}
            />
          </label>

          {/* Only offered when there is more than one band to choose between:
              a select with a single option is a control that does nothing. */}
          {bands.length > 1 && (
            <label className="cl-match__band">
              <span>Band</span>
              <select value={band} onChange={(e) => change(setBand)(e.target.value)}>
                <option value="">All bands</option>
                {bands.map((b) => (
                  <option key={b.year} value={b.year}>
                    {b.year}+ ≤ {b.max_price.toLocaleString()} {String(b.currency || "").toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Hidden when there is no median. Below three comparable quotes the
              backend returns null rather than guess, and a filter that cannot
              answer is worse than one that is not there. */}
          {medianBase != null && (
            <Toggle
              size="sm"
              checked={underpriced}
              onChange={change(setUnderpriced)}
              label="Underpriced only"
            />
          )}
        </div>

        {/* The feed's own filters, on the same messages (Alex, 2026-10-12).
            Every one of them is offered only when the data can answer it: a
            Country chooser listing 120 countries for a reference quoted from
            three is a list you have to read before you can use it, and the
            facets come back with the matches for exactly that reason. */}
        <div className="cl-match__filter-row">
          {regions.length > 1 && (
            <label className="cl-match__band">
              <span>Region</span>
              <select
                value={region}
                onChange={(e) => {
                  // A region and a country are two ways of saying the same
                  // thing to the backend, which only speaks countries[]. One
                  // clears the other rather than letting both narrow at once
                  // and produce an empty result nobody asked for.
                  change(setCountry)("");
                  change(setRegion)(e.target.value);
                }}
              >
                <option value="">All regions</option>
                {regions.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label} ({r.count})
                  </option>
                ))}
              </select>
            </label>
          )}

          {facetCountries.length > 1 && (
            <label className="cl-match__band">
              <span>Country</span>
              <select
                value={country}
                onChange={(e) => {
                  change(setRegion)("");
                  change(setCountry)(e.target.value);
                }}
              >
                <option value="">All countries</option>
                {facetCountries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} ({c.count})
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="cl-match__band">
            <span>When</span>
            <select value={period} onChange={(e) => change(setPeriod)(e.target.value)}>
              {PERIODS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          {facetGroups.length > 1 && (
            <details className="cl-match__groups">
              <summary>
                Hide groups
                {hiddenGroups.length > 0 ? ` (${hiddenGroups.length})` : ""}
              </summary>
              <div className="cl-match__groups-list">
                {facetGroups.map((g) => {
                  const hidden = hiddenGroups.includes(g.title);
                  return (
                    <label key={g.title} className="cl-match__group-row">
                      <input
                        type="checkbox"
                        checked={hidden}
                        onChange={() =>
                          change(setHiddenGroups)(
                            hidden
                              ? hiddenGroups.filter((t) => t !== g.title)
                              : [...hiddenGroups, g.title]
                          )
                        }
                      />
                      <span title={g.title}>{g.title}</span>
                      <b>{g.count}</b>
                    </label>
                  );
                })}
              </div>
            </details>
          )}

          <Toggle
            size="sm"
            checked={mediaAndText}
            onChange={change(setMediaAndText)}
            label="Picture + text"
          />
          <Toggle size="sm" checked={hideBulk} onChange={change(setHideBulk)} label="Hide bulk" />
        </div>
        </>
        )}
      </div>

      {feedTab ? (
        <CoreListTradeFeed reference={reference} />
      ) : isError ? (
        <StatePanel
          state="error"
          title="Could not load the matches"
          description="The prices are still on the reference; this view could not reach them."
        />
      ) : total === 0 && !isFetching ? (
        <StatePanel
          state="empty"
          title={hasTargets ? "Nothing here" : "No quotes yet"}
          description={
            q || underpriced || band
              ? "No quote in this bucket matches those filters."
              : hasTargets
                ? "No trade quote has landed in this bucket for this reference."
                : "No dealer has quoted this reference in a trade group yet."
          }
        />
      ) : (
        <>
          <div className={`cl-match__grid${isFetching ? " is-busy" : ""}`}>
            {items.map((row) =>
              // A price whose message we could not load still shows: the
              // quote is real and the merchant can still act on the supplier
              // columns. Silently dropping it would under-report the count
              // the card promised.
              row.message ? (
                <TradeFeedCard
                  key={row.product_price_id}
                  item={row.message}
                  query={debouncedQ}
                  onOpen={() => setOpenRow(row)}
                  extra={<PriceStrip row={row} />}
                  // A trade price is almost always read out of a TEXT price
                  // list, which carries no photo — so without this the grid is
                  // a wall of empty boxes (Alex, 2026-10-12). The card labels
                  // it "Catalogue photo" rather than implying the dealer sent
                  // one.
                  fallbackImage={productImageUrl}
                />
              ) : (
                <article className="leads-grid-card trade-feed-card cl-match__orphan" key={row.product_price_id}>
                  <PriceStrip row={row} />
                  <p className="cl-match__orphan-note">
                    The original message is no longer in the feed, so only the price we
                    extracted is left.
                  </p>
                  <span className="cl-match__orphan-id">msg {String(row.listing_code || "—").slice(-12)}</span>
                </article>
              )
            )}
          </div>

          {pages > 1 && (
            <nav className="cl-match__pager">
              <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <span>
                Page {page} of {pages} · {total.toLocaleString()} quotes
              </span>
              <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </nav>
          )}
        </>
      )}

      <TradeFeedModal
        item={openRow?.message || null}
        query={debouncedQ}
        onClose={() => setOpenRow(null)}
        extra={openRow ? <PriceStrip row={openRow} /> : null}
      />
    </div>
  );
};

export default CoreListMatchView;
